<template>
  <div class="intake-shell">
    <section class="hero-panel">
      <div>
        <p class="eyebrow">项目创建</p>
        <h1>先选项目类型，再开始创建项目</h1>
        <p class="hero-subtitle">如果你已经有明确的产品需求，就选产品驱动型；如果你想先做出一个故事或段子，再把产品自然带进去，就选内容驱动型。</p>
      </div>
      <div class="hero-actions">
        <el-button @click="router.push('/dashboard')">返回项目列表</el-button>
        <el-button v-if="createdProjectId" type="primary" plain @click="navigateToWorkflow(createdProjectId)">查看项目进度</el-button>
      </div>
    </section>

    <section class="mode-panel workspace-card">
      <div class="section-head section-head--compact">
        <div>
          <span class="section-label">项目类型</span>
          <h2>选择项目类型</h2>
          <p>选好之后，下面的填写内容会自动切换成对应方式，让你按最顺手的方式创建项目。</p>
        </div>
      </div>

      <div class="mode-grid">
        <button
          v-for="option in PROJECT_MODE_OPTIONS"
          :key="option.value"
          type="button"
          class="mode-card"
          :class="{ 'mode-card--active': formData.projectMode === option.value }"
          @click="handleModeSelect(option.value)"
        >
          <span>{{ option.label }}</span>
          <strong>{{ getProjectModeHeadline(option.value) }}</strong>
          <p>{{ option.description }}</p>
        </button>
      </div>
    </section>

    <section v-if="modeSelected" class="workspace-card form-card">
        <el-form ref="formRef" :model="formData" :rules="activeRules" label-position="top" class="requirement-form">
          <div class="step-panel step-panel--intro">
            <div class="section-head section-head--compact">
              <div>
                <span class="section-label">第一步</span>
                <h2>{{ firstStepTitle }}</h2>
                <p>{{ firstStepDescription }}</p>
              </div>
            </div>
          </div>

          <div class="form-grid form-grid--top">
            <el-form-item label="项目名称" prop="projectName">
              <el-input v-model="formData.projectName" maxlength="80" show-word-limit placeholder="例如：智能水杯内容种草计划" />
            </el-form-item>
            <el-form-item label="项目类型">
              <el-input :model-value="getProjectModeLabel(formData.projectMode)" readonly />
            </el-form-item>
          </div>

          <template v-if="isProductDriven">
            <div class="form-grid">
              <el-form-item label="业务方向" prop="productScenario">
                <el-select v-model="formData.productScenario" placeholder="选择业务方向">
                  <el-option v-for="option in productScenarioOptions" :key="option.value" :label="option.label" :value="option.value" />
                </el-select>
              </el-form-item>
              <el-form-item label="目标时长" prop="duration">
                <TargetDurationField v-model="formData.duration" />
              </el-form-item>
              <el-form-item label="目标用户" prop="targetAudience">
                <el-input v-model="formData.targetAudience" placeholder="例如：儿童家长、白领女性、大学生" />
              </el-form-item>
              <el-form-item label="视觉风格偏好">
                <el-input v-model="formData.visualStyle" placeholder="例如：现代简约、真实质感、轻喜剧节奏" />
              </el-form-item>
            </div>

            <el-form-item label="产品/需求描述" prop="requirementText">
              <el-input
                v-model="formData.requirementText"
                type="textarea"
                :rows="8"
                maxlength="1500"
                show-word-limit
                placeholder="请描述产品类型、核心卖点、目标场景、品牌调性、竞品、希望传达的价值。"
              />
            </el-form-item>

            <div class="form-grid">
              <el-form-item label="参考素材链接">
                <div class="field-stack">
                  <el-input v-model="formData.referenceUrl" placeholder="可填参考视频、落地页或网盘链接" />
                  <ReferenceLinkActions :url="formData.referenceUrl" compact />
                  <p class="field-tip">这里不会当场抓取或预览链接内容，但会在后续方案、剧本和分镜生成时作为参考上下文一起带入。</p>
                </div>
              </el-form-item>
              <el-form-item label="补充说明">
                <el-input
                  v-model="formData.remarks"
                  type="textarea"
                  :rows="4"
                  maxlength="800"
                  show-word-limit
                  placeholder="例如：预算限制、交付节点、必须出现的卖点或禁用表达。"
                />
              </el-form-item>
            </div>

            <div class="brief-preview">
              <div>
                <span class="section-label">当前需求摘要</span>
                <div class="preview-pills">
                  <span v-for="pill in productPreviewPills" :key="pill" class="preview-pill">{{ pill }}</span>
                </div>
              </div>
              <p class="preview-copy">{{ formData.requirementText || '你填写的产品需求会实时显示在这里，方便检查是否表达清楚。' }}</p>
            </div>
          </template>

          <template v-else-if="isContentDriven">
            <div class="form-grid">
              <el-form-item label="内容类型" prop="contentType">
                <div class="content-type-grid">
                  <button
                    v-for="option in CONTENT_TYPE_OPTIONS"
                    :key="option"
                    type="button"
                    class="content-type-chip"
                    :class="{ 'content-type-chip--active': formData.contentType === option }"
                    @click="formData.contentType = option"
                  >
                    {{ option }}
                  </button>
                </div>
              </el-form-item>
              <div class="form-grid form-grid--stacked">
                <el-form-item label="目标时长">
                  <TargetDurationField v-model="formData.duration" />
                </el-form-item>
                <el-form-item label="补充说明">
                  <el-input v-model="formData.remarks" maxlength="500" show-word-limit placeholder="可选：补充语气、禁用表达或额外要求" />
                </el-form-item>
              </div>
            </div>

            <ContentDrivenCreationSection
              :form-data="formData"
              :content-generating-action="contentGeneratingAction"
              :content-waiting-for-first-chunk="contentWaitingForFirstChunk"
              :is-generating-content="isGeneratingContent"
              :show-custom-prompt="showCustomPrompt"
              :custom-prompt="customPrompt"
              :can-confirm-content="canConfirmContent"
              :content-confirming="contentConfirming"
              :content-meta="contentMeta"
              :content-length="contentLength"
              :content-confirmed="contentConfirmed"
              :content-creation-mode="contentCreationMode"
              :integration-hint="integrationHint"
              :can-start-integration="canStartIntegration"
              :content-embedding="contentEmbedding"
              :content-embedding-waiting-for-first-chunk="contentEmbeddingWaitingForFirstChunk"
              :has-embedded-content="hasEmbeddedContent"
              :source-content="sourceContent"
              :embedded-content="embeddedContent"
              :get-content-source-label="getContentSourceLabel"
              @generate-content="handleGenerateContent"
              @update:show-custom-prompt="showCustomPrompt = $event"
              @update:custom-prompt="customPrompt = $event"
              @confirm-content="handleConfirmContent"
              @content-body-input="handleContentBodyInput"
              @update:content-creation-mode="contentCreationMode = $event"
              @integration-inputs-changed="handleIntegrationInputsChanged"
              @embed-product="handleEmbedProduct"
              @update:embedded-content="embeddedContent = $event"
              @embedded-content-input="handleEmbeddedContentInput"
            />
                </template>

                <template v-else-if="isScriptImport">
                  <div class="form-grid">
                    <el-form-item label="剧本标题（可选）">
                      <el-input v-model="formData.scriptTitle" maxlength="120" show-word-limit placeholder="例如：第 1 集《婚礼主桌反转》" />
                    </el-form-item>
                    <el-form-item label="目标时长（可选）">
                      <TargetDurationField v-model="formData.duration" />
                    </el-form-item>
                  </div>

                  <div class="form-grid">
                    <el-form-item label="目标用户（可选）">
                      <el-input v-model="formData.targetAudience" placeholder="例如：都市女性、亲子家庭、大学生" />
                    </el-form-item>
                    <el-form-item label="视觉风格（可选）">
                      <el-input v-model="formData.visualStyle" placeholder="例如：都市电影感、生活流写实、轻喜剧节奏" />
                    </el-form-item>
                  </div>

                  <div class="step-panel script-import-panel">
                    <div class="section-head section-head--compact">
                      <div>
                        <span class="section-label">第二步</span>
                        <h2>导入剧本，直接从剧本审批开始</h2>
                        <p>你可以直接粘贴剧本，或导入 txt / md 文件。创建后不会再走需求分析和方案确认，而是直接进入剧本修订审批。</p>
                      </div>
                    </div>

                    <div class="content-toolbar">
                      <div class="content-toolbar__group">
                        <el-button class="toolbar-button toolbar-button--primary" @click="triggerScriptUpload">导入剧本文件</el-button>
                        <el-button class="toolbar-button" plain :disabled="!formData.scriptBody.trim()" @click="clearImportedScript">清空剧本</el-button>
                      </div>
                      <div class="content-toolbar__group content-toolbar__group--end">
                        <span class="status-pill">{{ scriptImportSourceLabel }}</span>
                      </div>
                    </div>

                    <div class="status-grid">
                      <article class="status-card">
                        <span>当前字数</span>
                        <strong>{{ scriptBodyLength }}</strong>
                      </article>
                      <article class="status-card">
                        <span>导入来源</span>
                        <strong>{{ scriptImportSourceLabel }}</strong>
                      </article>
                      <article class="status-card">
                        <span>创建后起点</span>
                        <strong>剧本审批</strong>
                      </article>
                      <article class="status-card">
                        <span>任务分配流程</span>
                        <strong>本次不接入</strong>
                      </article>
                    </div>

                    <el-form-item label="剧本正文" prop="scriptBody">
                      <el-input
                        v-model="formData.scriptBody"
                        type="textarea"
                        :rows="16"
                        maxlength="20000"
                        show-word-limit
                        placeholder="粘贴完整剧本，建议至少包含标题、角色、场景和对白。创建后会直接进入剧本审批工作台。"
                      />
                    </el-form-item>
                  </div>

                  <div class="brief-preview brief-preview--script-import">
                    <div>
                      <span class="section-label">创建后会怎么走</span>
                      <div class="preview-pills">
                        <span class="preview-pill">跳过需求分析</span>
                        <span class="preview-pill">跳过方案确认</span>
                        <span class="preview-pill">直接进入剧本审批</span>
                      </div>
                    </div>
                    <p class="preview-copy">{{ scriptImportHint }}</p>
                  </div>
                </template>

                <template v-else>
                  <div class="brief-preview brief-preview--script-import">
                    <p class="preview-copy">请选择一种创建方式后继续。</p>
                  </div>
                </template>

                <section v-if="shouldShowTaskPublishPanel" class="task-publish-panel">
                  <div class="section-head section-head--compact">
                    <div>
                      <span class="section-label">任务广场</span>
                      <h2>创建后同步发布为协作任务</h2>
                      <p>管理员完成需求提交后，可把这份需求直接发布到任务广场。普通用户领取时，会复制出自己的项目继续推进。</p>
                    </div>
                  </div>

                  <div class="task-publish-panel__controls">
                    <el-switch v-model="taskPublishForm.enabled" active-text="创建后发布到任务广场" inactive-text="仅创建项目" />
                    <el-switch v-model="taskPublishForm.allowMultipleClaims" active-text="允许多人领取" inactive-text="仅允许单人领取" />
                  </div>

                  <div v-if="taskPublishForm.enabled" class="task-publish-panel__grid">
                    <el-input v-model="taskPublishForm.title" maxlength="120" show-word-limit placeholder="任务标题" />
                    <el-input v-model="taskPublishForm.summary" maxlength="180" show-word-limit placeholder="任务摘要，让领取人快速理解这单要做什么" />
                  </div>
                </section>

                <div class="action-row">
                  <div class="action-hint">
                    <span class="hint-title">创建后会发生什么</span>
                    <span>{{ submitHint }}</span>
                  </div>
                  <GenerationModelSplitButton
                    v-if="!isScriptImport"
                    v-model="proposalTextModelId"
                    :action-label="submitButtonLabel"
                    :options="proposalTextModelOptions"
                    size="large"
                    :loading="submitting"
                    :disabled="submitDisabled"
                    @trigger="handleSubmit"
                  />
                  <el-button v-else type="primary" size="large" :loading="submitting" :disabled="submitDisabled" @click="handleSubmit">
                    {{ submitButtonLabel }}
                  </el-button>
                </div>
              </el-form>
            </section>

          <section v-else class="workspace-card empty-state-card">
            <span class="section-label">下一步</span>
            <h2>请选择项目类型</h2>
            <p>先告诉系统你想按哪种方式创建项目，页面就会自动切换成对应的填写内容。</p>
          </section>

    <input
      ref="scriptUploadInput"
      class="reference-upload-input"
      type="file"
      accept=".txt,.md,text/plain,text/markdown"
      @change="handleScriptFileSelected"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import type { FormInstance, FormRules } from 'element-plus'
import { ElMessage } from 'element-plus'

import GenerationModelSplitButton from '@/components/GenerationModelSplitButton.vue'
import ReferenceLinkActions from '@/components/ReferenceLinkActions.vue'
import { confirmContentSeed, streamEmbedProductIntoContent, streamGenerateContentSeed } from '@/api/workflow'
import { useModelCatalogStore } from '@/stores/modelCatalog'
import { useProjectStore } from '@/stores/project'
import { useUserStore } from '@/stores/user'
import { resolveGenerationModelSelection, setGenerationModelSelection } from '@/utils/generationModelSelection'
import { type ProjectMode, CONTENT_TYPE_OPTIONS, PROJECT_MODE_OPTIONS, getContentSourceLabel, getProjectModeLabel } from '@/utils/projectModes'
import ContentDrivenCreationSection from './submit-requirement/ContentDrivenCreationSection.vue'
import TargetDurationField from './submit-requirement/TargetDurationField.vue'
import { useSubmitRequirementPageState } from './submit-requirement/useSubmitRequirementPageState'
import { useSubmitRequirementSubmission } from './submit-requirement/useSubmitRequirementSubmission'
import { useScriptImportInputs } from './submit-requirement/useScriptImportInputs'
import { useStreamedEditor } from './submit-requirement/useStreamedEditor'

const productScenarioOptions = [
  { label: '产品广告', value: 'product_ad' },
  { label: '品牌宣传', value: 'brand_promo' },
  { label: '教育培训', value: 'education' },
  { label: '社交媒体内容', value: 'social_media' },
  { label: '其他', value: 'other' },
]

const router = useRouter()
const projectStore = useProjectStore()
const userStore = useUserStore()
const modelCatalogStore = useModelCatalogStore()
const PROPOSAL_TEXT_OPERATION = 'workflow_proposal_generate'

const formRef = ref<FormInstance>()
const submitting = ref(false)
const createdProjectId = ref<number | null>(null)
const contentGeneratingAction = ref<'generate' | 'refresh' | null>(null)
const contentConfirming = ref(false)
const contentEmbedding = ref(false)
const contentWaitingForFirstChunk = ref(false)
const contentEmbeddingWaitingForFirstChunk = ref(false)
const scriptUploadInput = ref<HTMLInputElement | null>(null)
const sourceContent = ref('')
const embeddedContent = ref('')
const hasEmbeddedContent = ref(false)
const contentCreationMode = ref<'direct' | 'embed'>('embed')
const contentConfirmed = ref(false)
const showCustomPrompt = ref(false)
const customPrompt = ref('')

const contentMeta = reactive({
  source: '',
  libraryCount: 0,
  updatedAt: '',
  fromLibrary: false,
})
const taskPublishForm = reactive({
  enabled: true,
  title: '',
  summary: '',
  allowMultipleClaims: true,
})
const proposalTextModelId = ref('')

const formData = reactive({
  projectName: '',
  projectMode: '' as ProjectMode | '',
  productScenario: '',
  duration: 1,
  targetAudience: '',
  requirementText: '',
  visualStyle: '',
  referenceUrl: '',
  remarks: '',
  contentType: '',
  contentBody: '',
  productName: '',
  productFeatures: '',
  productScene: '',
  scriptTitle: '',
  scriptBody: '',
})

const {
  activeStreamTarget,
  draftEditorWaitingMessage,
  embeddedEditorWaitingMessage,
  resetStreamRenderer,
  clearWaitingMessageInEditor,
  showWaitingMessageInEditor,
  enqueueStreamingText,
  replaceStreamingText,
  waitForStreamingRender,
} = useStreamedEditor({
  getText: (target) => (target === 'draft' ? formData.contentBody : embeddedContent.value),
  setText: (target, value) => {
    if (target === 'draft') {
      formData.contentBody = value
      return
    }
    embeddedContent.value = value
  },
})

const productDrivenRules: FormRules = {
  projectName: [{ required: true, message: '请输入项目名称', trigger: 'blur' }],
  productScenario: [{ required: true, message: '请选择业务方向', trigger: 'change' }],
  duration: [{ required: true, message: '请选择目标时长', trigger: 'change' }],
  targetAudience: [{ required: true, message: '请输入目标用户', trigger: 'blur' }],
  requirementText: [{ required: true, message: '请填写产品需求描述', trigger: 'blur' }],
}

const contentDrivenRules: FormRules = {
  projectName: [{ required: true, message: '请输入项目名称', trigger: 'blur' }],
  contentType: [{ required: true, message: '请选择内容类型', trigger: 'change' }],
}

const scriptImportRules: FormRules = {
  projectName: [{ required: true, message: '请输入项目名称', trigger: 'blur' }],
  scriptBody: [{ required: true, message: '请粘贴或导入剧本正文', trigger: 'blur' }],
}

const {
  activeRules,
  canConfirmContent,
  canStartIntegration,
  contentLength,
  currentProductScenarioLabel,
  firstStepDescription,
  firstStepTitle,
  integrationHint,
  isContentDriven,
  isGeneratingContent,
  isProductDriven,
  isScriptImport,
  isShowingEditorWaitingMessage,
  isShowingEmbeddedWaitingMessage,
  modeSelected,
  productPreviewPills,
  requiresContentEmbedding,
  resolvedContentSubmission,
  shouldShowTaskPublishPanel,
  submitButtonLabel,
  submitDisabled,
  submitHint,
  taskPublishSummarySource,
} = useSubmitRequirementPageState({
  formData,
  productScenarioOptions,
  productDrivenRules,
  contentDrivenRules,
  scriptImportRules,
  contentCreationMode,
  embeddedContent,
  draftEditorWaitingMessage,
  embeddedEditorWaitingMessage,
  contentConfirming,
  contentConfirmed,
  contentEmbedding,
  contentGeneratingAction,
  hasEmbeddedContent,
  taskPublishEnabled: computed(() => taskPublishForm.enabled),
  isAdmin: computed(() => userStore.isAdmin),
})
const {
  clearImportedScript,
  handleScriptFileSelected,
  scriptBodyLength,
  scriptImportHint,
  scriptImportSource,
  scriptImportSourceLabel,
  triggerScriptUpload,
} = useScriptImportInputs({
  formData,
  scriptUploadInput,
  success: (message) => ElMessage.success(message),
  error: (message) => ElMessage.error(message),
})
const {
  handleSubmit,
  navigateToWorkflow,
} = useSubmitRequirementSubmission({
  router,
  formRef,
  submitting,
  createdProjectId,
  formData,
  contentCreationMode,
  modeSelected,
  isContentDriven,
  isScriptImport,
  requiresContentEmbedding,
  resolvedContentSubmission,
  shouldShowTaskPublishPanel,
  hasEmbeddedContent,
  contentConfirmed,
  sourceContent,
  embeddedContent,
  contentMeta,
  scriptImportSource,
  scriptImportSourceLabel,
  proposalTextModelId,
  taskPublishForm,
  addProject: (project) => projectStore.addProject(project),
  success: (message) => ElMessage.success(message),
  error: (message) => ElMessage.error(message),
  warning: (message) => ElMessage.warning(message),
})

const proposalTextModelOptions = computed(() => modelCatalogStore.textModelsForOperation(PROPOSAL_TEXT_OPERATION))

watch(
  proposalTextModelOptions,
  (options) => {
    proposalTextModelId.value = resolveGenerationModelSelection('text', PROPOSAL_TEXT_OPERATION, options)
  },
  { immediate: true }
)

watch(proposalTextModelId, (value) => {
  setGenerationModelSelection('text', PROPOSAL_TEXT_OPERATION, value)
})

onMounted(() => {
  void modelCatalogStore.ensureLoaded()
})

watch(
  () => [formData.projectName, formData.requirementText, formData.contentBody, embeddedContent.value, formData.projectMode, formData.scriptBody, contentCreationMode.value],
  () => {
    taskPublishForm.title = formData.projectName.trim() || taskPublishForm.title
    taskPublishForm.summary = taskPublishSummarySource.value.slice(0, 180)
  },
  { immediate: true }
)

const handleModeSelect = (mode: ProjectMode) => {
  formData.projectMode = mode
  if (mode === 'script_import') {
    taskPublishForm.enabled = false
  } else if (userStore.isAdmin) {
    taskPublishForm.enabled = true
  }
  formRef.value?.clearValidate()
}

const getProjectModeHeadline = (mode: ProjectMode) => {
  if (mode === 'script_import') {
    return '直接导入剧本进入审批'
  }
  if (mode === 'content_driven') {
    return '先定内容再带产品'
  }
  return '按产品需求创建'
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return '刚刚'
  }
  return new Date(value).toLocaleString('zh-CN')
}

const handleContentBodyInput = () => {
  if (formData.contentBody.trim() !== sourceContent.value.trim()) {
    contentConfirmed.value = false
  }
  hasEmbeddedContent.value = false
}

const handleEmbeddedContentInput = () => {
  hasEmbeddedContent.value = Boolean(embeddedContent.value.trim())
}

const handleIntegrationInputsChanged = () => {
  hasEmbeddedContent.value = false
}

const handleGenerateContent = async ({ forceRefresh, textModelId }: { forceRefresh: boolean; textModelId?: string }) => {
  if (!formData.contentType) {
    ElMessage.warning('先选一个内容类型')
    return
  }

  contentGeneratingAction.value = forceRefresh ? 'refresh' : 'generate'
  contentWaitingForFirstChunk.value = true
  try {
    activeStreamTarget.value = 'draft'
    showWaitingMessageInEditor('生成中，请稍后。')
    hasEmbeddedContent.value = false
    embeddedContent.value = ''
    contentConfirmed.value = false

    const response = await streamGenerateContentSeed({
      content_type: formData.contentType,
      force_refresh: forceRefresh,
      custom_prompt: customPrompt.value.trim(),
      text_model_id: textModelId,
      operation_type: 'content_seed_generate',
    }, {
      onMeta: (payload) => {
        contentMeta.source = payload.source
        contentMeta.libraryCount = payload.library_count
        contentMeta.updatedAt = payload.updated_at || ''
        contentMeta.fromLibrary = payload.from_library
      },
      onChunk: ({ text }) => {
        contentWaitingForFirstChunk.value = false
        enqueueStreamingText(text)
      },
      onReplace: ({ content }) => {
        contentWaitingForFirstChunk.value = false
        replaceStreamingText(content)
      },
    })

    await waitForStreamingRender()
    formData.contentBody = response.content
    sourceContent.value = response.content
    contentConfirmed.value = response.from_library
    contentMeta.source = response.source
    contentMeta.libraryCount = response.library_count
    contentMeta.updatedAt = response.updated_at || ''
    contentMeta.fromLibrary = response.from_library
    ElMessage.success(response.from_library ? '已从素材参考中心带入一版内容' : forceRefresh ? '已经为你换了一版新内容，确认后才会加入素材参考中心' : '内容初稿已经生成，可以继续修改')
  } catch (error: any) {
    clearWaitingMessageInEditor()
    ElMessage.error(error?.message || error?.response?.data?.detail || '内容生成失败，请稍后再试')
  } finally {
    contentWaitingForFirstChunk.value = false
    resetStreamRenderer()
    contentGeneratingAction.value = null
  }
}

const handleConfirmContent = async () => {
  if (!formData.contentType) {
    ElMessage.warning('先选一个内容类型')
    return
  }
  if (!formData.contentBody.trim()) {
    ElMessage.warning('先生成一版内容，或者先写入内容')
    return
  }

  contentConfirming.value = true
  try {
    const currentContent = formData.contentBody.trim()
    const response = await confirmContentSeed({
      content_type: formData.contentType,
      content: currentContent,
      source: contentMeta.source || 'manual_confirmed',
    })
    sourceContent.value = currentContent
    contentConfirmed.value = true
    contentMeta.libraryCount = response.library_count
    contentMeta.updatedAt = response.saved_at || contentMeta.updatedAt
    ElMessage.success('这版内容已加入素材参考中心')
  } catch (error: any) {
    ElMessage.error(error?.response?.data?.detail || '加入素材参考中心失败')
  } finally {
    contentConfirming.value = false
  }
}

const handleEmbedProduct = async ({ textModelId }: { textModelId?: string }) => {
  if (!formData.contentBody.trim()) {
    ElMessage.warning('先准备好要改的内容')
    return
  }
  if (!formData.productName.trim() || !formData.productFeatures.trim()) {
    ElMessage.warning('先填好产品名称和产品卖点')
    return
  }

  contentEmbedding.value = true
  contentEmbeddingWaitingForFirstChunk.value = true
  try {
    const currentContent = formData.contentBody.trim()
    sourceContent.value = currentContent
    activeStreamTarget.value = 'embedded'
    showWaitingMessageInEditor('整合中，请稍后。')
    const response = await streamEmbedProductIntoContent({
      product_name: formData.productName.trim(),
      product_features: formData.productFeatures.trim(),
      product_scene: formData.productScene.trim(),
      content: currentContent,
      text_model_id: textModelId,
      operation_type: 'content_embed',
    }, {
      onChunk: ({ text }) => {
        contentEmbeddingWaitingForFirstChunk.value = false
        enqueueStreamingText(text)
      },
    })
    await waitForStreamingRender()
    embeddedContent.value = response.content
    hasEmbeddedContent.value = true
    ElMessage.success('内容整合完成，产品信息已经自然融入当前内容')
  } catch (error: any) {
    clearWaitingMessageInEditor()
    ElMessage.error(error?.response?.data?.detail || '产品信息融合失败，请稍后再试')
  } finally {
    contentEmbeddingWaitingForFirstChunk.value = false
    resetStreamRenderer()
    contentEmbedding.value = false
  }
}

</script>

<style scoped>
.intake-shell {
  min-height: 100vh;
  padding: 28px;
  background:
    radial-gradient(circle at top left, rgba(251, 191, 36, 0.22), transparent 28%),
    radial-gradient(circle at bottom right, rgba(14, 165, 233, 0.16), transparent 30%),
    linear-gradient(180deg, #f7f7f2 0%, #edf3f7 100%);
}

.hero-panel,
.workspace-card {
  max-width: 1400px;
  margin: 0 auto;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 28px 70px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(16px);
}

.hero-panel {
  padding: 28px 32px;
  display: flex;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 22px;
}

.hero-panel h1,
.section-head h2,
.empty-state-card h2 {
  margin: 0;
  color: #10243d;
  letter-spacing: -0.04em;
}

.hero-panel h1 {
  font-size: 38px;
}

.hero-subtitle,
.section-head p,
.empty-state-card p {
  margin: 12px 0 0;
  color: #526173;
  line-height: 1.75;
}

.hero-actions {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.eyebrow,
.section-label,
.hint-title {
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a5a1f;
}

.mode-panel {
  padding: 26px 28px;
  margin-bottom: 22px;
}

.section-head {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-start;
}

.section-head--compact h2 {
  font-size: 26px;
}

.mode-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-top: 20px;
}

.mode-card,
.content-type-chip {
  border: 1px solid rgba(148, 163, 184, 0.24);
  background: #f8fbfd;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.mode-card {
  padding: 20px;
  border-radius: 22px;
  text-align: left;
  cursor: pointer;
}

.mode-card span,
.mode-card strong {
  display: block;
}

.mode-card span {
  font-size: 14px;
  color: #8a5a1f;
}

.mode-card strong {
  margin-top: 10px;
  font-size: 24px;
  color: #10243d;
}

.mode-card p {
  margin: 12px 0 0;
  color: #526173;
  line-height: 1.7;
}

.mode-card--active,
.content-type-chip--active {
  border-color: rgba(13, 148, 136, 0.45);
  box-shadow: 0 16px 36px rgba(15, 118, 110, 0.14);
  transform: translateY(-2px);
  background: linear-gradient(135deg, rgba(240, 253, 250, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%);
}

.workspace-card {
  padding: 28px;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.status-card {
  padding: 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
}

.status-card span {
  display: block;
  color: #64748b;
  font-size: 13px;
}

.status-card strong {
  display: block;
  margin-top: 10px;
  color: #10243d;
  font-size: 20px;
  line-height: 1.35;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.form-grid--top {
  margin-bottom: 8px;
}

.form-grid--stacked {
  display: grid;
  gap: 18px;
}

.reference-section,
.knowledge-linker,
.script-import-panel {
  display: grid;
  gap: 18px;
  margin-top: 8px;
  margin-bottom: 22px;
}

.action-row {
  display: flex;
  gap: 14px;
}

.reference-card__fields,
.action-hint,
.content-toolbar,
.integration-panel__copy,
.field-stack {
  display: grid;
  gap: 10px;
}

.field-tip {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.7;
}

.brief-preview,
.empty-state-card {
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.18);
}
.preview-copy {
  color: #475569;
  line-height: 1.75;
}

.preview-pills,
.content-type-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.content-type-grid {
  width: 100%;
}

.content-type-chip {
  border-radius: 999px;
  padding: 12px 18px;
  cursor: pointer;
  color: #24415f;
}

.hero-actions,
.next-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.step-panel {
  padding: 24px;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.96));
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.05);
}

.step-panel--intro {
  margin-bottom: 22px;
}

.content-toolbar {
  gap: 14px;
}

.content-toolbar__group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.content-toolbar__group--end {
  justify-content: flex-start;
}

.toolbar-button {
  min-height: 44px;
  padding-inline: 18px;
  border-radius: 999px;
}

.toolbar-button--primary {
  box-shadow: 0 14px 30px rgba(37, 99, 235, 0.18);
}

.status-pill,
.preview-pill {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #0f766e;
  font-size: 13px;
}

.brief-preview {
  padding: 20px;
  background: linear-gradient(135deg, #fffbeb 0%, #f0fdfa 100%);
}

.brief-preview--script-import {
  background: linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%);
}

.task-publish-panel {
  margin-top: 24px;
  padding: 22px;
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(239, 246, 255, 0.92), rgba(255, 251, 235, 0.96));
  border: 1px solid rgba(59, 130, 246, 0.16);
  display: grid;
  gap: 16px;
}

.task-publish-panel__controls,
.task-publish-panel__grid {
  display: grid;
  gap: 12px;
}

.task-publish-panel__grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.preview-copy {
  margin: 14px 0 0;
  white-space: pre-wrap;
}

.action-row {
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
}

.action-hint {
  max-width: 720px;
  color: #64748b;
}

.reference-upload-input {
  display: none;
}

.empty-state-card {
  max-width: 1400px;
  margin: 0 auto;
  padding: 34px 28px;
  text-align: center;
}

@media (max-width: 1100px) {
  .status-grid,
  .task-publish-panel__grid {
    grid-template-columns: 1fr;
  }

  .mode-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .intake-shell {
    padding: 16px;
  }

  .hero-panel,
  .workspace-card,
  .mode-panel {
    padding: 22px;
  }

  .hero-panel,
  .form-grid,
  .action-row,
  .hero-actions,
  .content-toolbar__group {
    display: grid;
    grid-template-columns: 1fr;
  }

  .status-grid {
    grid-template-columns: 1fr;
  }

  .hero-panel h1 {
    font-size: 30px;
  }
}
</style>